import { useRef, useCallback, type RefObject, type ChangeEvent } from 'react';

export interface UseFilePickerOptions {
  onFilesSelected: (files: File[]) => void;
}

export interface UseFilePickerReturn {
  open: () => void;
  inputRef: RefObject<HTMLInputElement>;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

/**
 * 隱藏的 `<input type="file">` 的接線：input 由呼叫端渲染，這裡提供觸發與選檔處理。
 *
 * @example
 * const { open, inputRef, handleChange } = useFilePicker({ onFilesSelected });
 *
 * <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleChange} hidden />
 * <button onClick={open}>選擇照片</button>
 */
export function useFilePicker({
  onFilesSelected,
}: UseFilePickerOptions): UseFilePickerReturn {
  const inputRef = useRef<HTMLInputElement>(null);

  const open = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        onFilesSelected(Array.from(e.target.files));
        /* 清空 value 確保重複選擇同一檔案仍觸發 onChange */
        e.target.value = '';
      }
    },
    [onFilesSelected],
  );

  return { open, inputRef, handleChange };
}
